import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MapIcon from '@mui/icons-material/Map';
import PaidIcon from '@mui/icons-material/Paid';
import { useAuth } from '../contexts/AuthContext';
import { useAnalyticsExposure } from '../contexts/AnalyticsContext';

const accent = '#64ffda';
const plusAccent = '#54e8c7';
const panel = '#242424';
const panelSoft = 'rgba(255, 255, 255, 0.035)';
const border = 'rgba(255, 255, 255, 0.09)';

const priceByInterval = {
  monthly: {
    plus: {
      price: '29 zł / m-c',
      period: 'Rozliczenie miesięczne, anuluj kiedy chcesz',
    },
  },
  yearly: {
    plus: {
      price: '20,83 zł / m-c',
      period: 'Rozliczenie roczne - 250 zł zamiast 348 zł',
    },
  },
};

const freeFeatures = [
  'Terminarze i wyniki',
  'Zaawansowane tabele i kalkulatory ligowe',
  'Dane predykcyjne dla lig centralnych',
  'Ranking Elo szczebla centralnego',
];

const plusFeatures = [
  'Wszystko z pakietu Free',
  'Predykcje dla wszystkich lig seniorskich w Polsce',
  'Ranking Elo - cała Polska',
  'Wykresy szans i trendów',
  'Własne symulacje i laboratorium scenariuszy',
  'Porównania symulacji',
  'Analiza play-offów i trudności terminarza',
  'Analiza kursów bukmacherskich',
];

const buildPlans = (billingInterval, user, hasActiveVip) => [
  {
    name: 'Free',
    key: 'free',
    icon: AssessmentIcon,
    badge: 'Start',
    price: '0 zł',
    period: 'bez opłat',
    accentColor: '#b0b0b0',
    description: 'Darmowy dostęp do najważniejszych danych i predykcji lig centralnych',
    features: freeFeatures,
    note: 'Więcej analiz i narzędzi odblokujesz w pakiecie VIP.',
    cta: user && !hasActiveVip ? 'Twój obecny plan' : 'Korzystaj za darmo',
    to: '/',
    disabled: Boolean(user && !hasActiveVip),
  },
  {
    name: 'Symuliga VIP',
    key: 'plus',
    icon: MapIcon,
    badge: 'Pełny dostęp',
    price: priceByInterval[billingInterval].plus.price,
    period: priceByInterval[billingInterval].plus.period,
    accentColor: plusAccent,
    description:
      'Pełny dostęp do wszystkich lig, predykcji i narzędzi Symuligi — od Ekstraklasy po ligi regionalne.',
    features: plusFeatures,
    cta: 'Odblokuj pełen dostęp',
    to: `/kup-vip?plan=plus&interval=${billingInterval}`,
    active: hasActiveVip,
  },
];

function PlanFeature({ label, accentColor }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <CheckCircleIcon
        sx={{
          mt: '2px',
          fontSize: 20,
          color: accentColor,
          flex: '0 0 auto',
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: { xs: '15px !important', md: '16px !important' },
          lineHeight: '1.48 !important',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function PlanCard({ plan, highlighted = false }) {
  const Icon = plan.icon;
  const disabled = plan.active || plan.disabled;
  const ctaLabel = plan.active ? 'Pakiet aktywny' : plan.cta;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: 'auto',
        flex: 1,
        boxSizing: 'border-box',
        p: { xs: 2.25, md: 2.5 },
        borderRadius: 2,
        backgroundColor: highlighted ? 'rgba(100, 255, 218, 0.045)' : panelSoft,
        border: `1px solid ${highlighted ? 'rgba(100, 255, 218, 0.38)' : border}`,
        boxShadow: highlighted ? '0 0 24px rgba(100, 255, 218, 0.1)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        <Box sx={{ minHeight: { md: 230 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 1,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: `${plan.accentColor}14`,
                border: `1px solid ${plan.accentColor}33`,
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ color: plan.accentColor, fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#fff',
                  fontWeight: 900,
                  lineHeight: 1.15,
                  fontSize: { xs: '20px !important', md: '21px !important' },
                }}
              >
                {plan.name}
              </Typography>
              <Chip
                label={plan.badge}
                size="small"
                sx={{
                  mt: 0.75,
                  height: 22,
                  borderRadius: 1,
                  color: plan.accentColor,
                  backgroundColor: `${plan.accentColor}12`,
                  border: `1px solid ${plan.accentColor}26`,
                  fontWeight: 800,
                }}
              />
            </Box>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="h5"
              sx={{
                color: '#fff',
                fontWeight: 900,
                fontSize: { xs: '24px !important', md: '27px !important' },
              }}
            >
              {plan.price}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: plan.accentColor,
                fontWeight: 800,
                fontSize: { xs: '15px !important', md: '16px !important' },
                lineHeight: '1.55 !important',
              }}
            >
              {plan.period}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: 'text.secondary',
              fontSize: { xs: '16px !important', md: '17px !important' },
              lineHeight: '1.68 !important',
            }}
          >
            {plan.description}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: border }} />

        <Stack spacing={1.1}>
          {plan.features.map((item) => (
            <PlanFeature key={item} label={item} accentColor={plan.accentColor} />
          ))}
        </Stack>

        {plan.note && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.62)',
              fontSize: { xs: '15px !important', md: '16px !important' },
              lineHeight: '1.5 !important',
            }}
          >
            {plan.note}
          </Typography>
        )}

        <Button
          variant={highlighted ? 'contained' : 'outlined'}
          size="large"
          {...(!disabled ? { component: RouterLink, to: plan.to } : {})}
          disabled={disabled}
          sx={{
            mt: 'auto',
            py: 1.15,
            borderRadius: 1,
            color: highlighted ? '#101010' : '#fff',
            backgroundColor: highlighted ? plan.accentColor : 'transparent',
            borderColor: highlighted ? plan.accentColor : border,
            fontWeight: 900,
            fontSize: { xs: '15px !important', md: '16px !important' },
            textTransform: 'none',
            '&:hover': {
              backgroundColor: highlighted ? '#4be6c4' : `${plan.accentColor}12`,
              borderColor: plan.accentColor,
            },
            '&.Mui-disabled': {
              color: highlighted ? 'rgba(16, 16, 16, 0.72)' : 'rgba(255, 255, 255, 0.46)',
              backgroundColor: highlighted ? 'rgba(100, 255, 218, 0.42)' : 'rgba(255, 255, 255, 0.04)',
              borderColor: highlighted ? 'rgba(100, 255, 218, 0.18)' : border,
            },
          }}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Paper>
  );
}

export default function VipTeaser({ placement = 'vip_page', requirement }) {
  const { user, hasRole } = useAuth();
  const exposureRef = useAnalyticsExposure(
    placement === 'paywall' ? ['paywall_view', 'vip_offer_view'] : 'vip_offer_view',
    { placement, ...(requirement ? { requirement } : {}) }
  );
  const [billingInterval, setBillingInterval] = useState('monthly');
  const hasActiveVip = hasRole('admin') || hasRole('poland') || hasRole('poland_regions');
  const plans = useMemo(
    () => buildPlans(billingInterval, user, hasActiveVip),
    [billingInterval, user, hasActiveVip]
  );

  const handleIntervalChange = (event, nextInterval) => {
    if (nextInterval) {
      setBillingInterval(nextInterval);
    }
  };

  return (
    <Paper
      data-analytics-placement={placement}
      elevation={0}
      sx={{
        maxWidth: 1120,
        mx: 'auto',
        my: { xs: 3, md: 6 },
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: panel,
        color: '#fff',
        border: `1px solid ${border}`,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
      }}
    >
      <Box
        ref={exposureRef}
        sx={{
          p: { xs: 2.5, md: 4 },
          backgroundColor: '#1f1f1f',
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Chip
            icon={<PaidIcon sx={{ color: `${accent} !important` }} />}
            label="Pakiety dostępu"
            sx={{
              borderRadius: 1,
              color: '#fff',
              backgroundColor: 'rgba(100, 255, 218, 0.1)',
              border: '1px solid rgba(100, 255, 218, 0.22)',
              fontWeight: 800,
            }}
          />
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 900,
              color: '#fff',
              fontSize: { xs: '28px !important', md: '38px !important' },
              lineHeight: '1.15 !important',
            }}
          >
            Pełny dostęp do świata Symuligi
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 760,
              color: 'text.secondary',
              fontSize: { xs: '16px !important', md: '17px !important' },
              lineHeight: '1.72 !important',
            }}
          >
            Korzystaj z podstawowych danych za darmo albo odblokuj pełną analitykę
            Symuligi w pakiecie VIP.
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={billingInterval}
            onChange={handleIntervalChange}
            size="small"
            sx={{
              mt: 1,
              p: 0.35,
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${border}`,
              '& .MuiToggleButton-root': {
                minWidth: 118,
                border: 0,
                borderRadius: '6px !important',
                color: 'rgba(255, 255, 255, 0.72)',
                fontWeight: 900,
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: '#101010 !important',
                backgroundColor: `${accent} !important`,
              },
            }}
          >
            <ToggleButton value="monthly">Miesięcznie</ToggleButton>
            <ToggleButton value="yearly">Rocznie (-28%)</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2.5, md: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 3.25, md: 2.25 },
            alignItems: 'stretch',
            gridAutoRows: { xs: 'auto', md: '1fr' },
          }}
        >
          {plans.map((plan) => (
            <Box key={plan.name} sx={{ minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
              <PlanCard plan={plan} highlighted={plan.key === 'plus'} />
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

